import { VEHICLE_TYPE_VALUES, type VehicleType } from "~/lib/constants/vehicle";

/**
 * The vehicle size this browser last searched with.
 *
 * Only ever a fallback, and only for the one case that would otherwise need a
 * question: a signed-in driver with no vehicle on file who has already told us
 * once. The default vehicle takes precedence whenever there is one — this is a
 * memory, not a preference, and it must never contradict a real vehicle record.
 *
 * `localStorage` rather than either existing store because neither fits:
 * `searchDraftStore` isn't persisted at all (it's scoped to mounted editors),
 * and `bookingStore` is sessionStorage and only written after a hold exists.
 * The value survives a tab close, which is the point — the question is worth
 * asking once per browser, not once per visit.
 *
 * Every read is defensive. The value can be edited by hand, can predate a change
 * to the size list, and `localStorage` itself throws in Safari's private mode
 * and when storage is disabled — none of which is worth failing a click over.
 */

const STORAGE_KEY = "ezspot_last_vehicle_size";

function isVehicleType(value: string): value is VehicleType {
  return (VEHICLE_TYPE_VALUES as readonly string[]).includes(value);
}

export function readLastVehicleSize(): VehicleType | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    return isVehicleType(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeLastVehicleSize(size: VehicleType): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, size);
  } catch {
    // Storage unavailable — the driver is asked again next time, which is the
    // correct degradation. Never a reason to block the search they just started.
  }
}
